// src/pages/BisnisSaya.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { authHeaders } from "../utils/authHeaders";
import "./BisnisSaya.css";

const CATEGORIES = [
  "Bibit Kakao",
  "Bibit Buah dan Kayu",
  "Bahan Input",
  "Alat Pertanian",
  "Jasa",
  "Jual-Beli Hasil Bumi",
  "Konsultan",
];

const KAKAO_VARIETIES = [
  "MCC02/45",
  "BB01",
  "S2/BR25",
  "S1/PBC123",
  "MCC01/M01",
  "M04",
  "M06",
  "ISC1",
  "SCC11",
];

const JASA_OPTIONS = [
  "Pemangkasan",
  "Panen",
  "Polinasi",
  "Penyambungan",
  "Penanaman Ulang",
  "Pemupukan",
  "Pembuatan Kompos",
  "Pengendalian Hama & Penyakit",
  "Pembuatan Naungan",
  "Pendampingan Sertifikasi",
];

const ENV_API = process.env.REACT_APP_API_URL || "/api";
const CURRENT_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const IS_LOCAL_WEB =
  typeof window !== "undefined" && /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);
const API = (() => {
  const raw = String(ENV_API || "").trim();
  if (!/^https?:\/\//i.test(raw)) return raw || "/api";
  try {
    const u = new URL(raw);
    const isLocalApi = /^(localhost|127\.0\.0\.1)$/i.test(u.hostname);
    if (!IS_LOCAL_WEB && isLocalApi) return `${CURRENT_ORIGIN}/api`;
    if (typeof window !== "undefined" && window.location.protocol === "https:" && u.protocol === "http:") {
      return raw.replace(/^http:/i, "https:");
    }
    return raw;
  } catch {
    return raw || "/api";
  }
})();
const API_ORIGIN = API.startsWith("http")
  ? API.replace(/\/api\/?$/, "")
  : (CURRENT_ORIGIN || "");

const defaultForm = {
  id: null,
  category: "Bibit Kakao",
  kakaoSelected: [],
  kakaoOther: "",
  jasaSelected: "",
  jasaOther: "",
  title: "",
  capacity: "",
  unit_price: "",
  notes: "",
  status: "active",
  photo_file: null,
  photo_data_url: "",
  photo_url: "",
};

function formatRupiah(v) {
  if (typeof v !== "number" || !isFinite(v)) return "—";
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(v);
}

function formatWholeNumber(value) {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}

/**
 * ✅ Parse input harga ke integer Rupiah TANPA error "100000.00" => 100000 (bukan 10000000)
 * Mendukung:
 * - 100000
 * - 100.000
 * - 100000.00
 * - 100.000,00
 * - Rp 100.000
 */
function parseRupiahToInt(value) {
  if (value === null || value === undefined) return null;

  // jika number, pastikan integer rupiah
  if (typeof value === "number") {
    if (!isFinite(value)) return null;
    return Math.round(value);
  }

  let s = String(value).trim();
  if (!s) return null;

  // buang simbol Rp dan spasi
  s = s.replace(/rp/gi, "").replace(/\s+/g, "");

  const hasDot = s.includes(".");
  const hasComma = s.includes(",");

  // kasus: ada '.' dan ',' -> tentukan mana desimal berdasarkan posisi terakhir
  if (hasDot && hasComma) {
    const lastDot = s.lastIndexOf(".");
    const lastComma = s.lastIndexOf(",");

    if (lastComma > lastDot) {
      // format ID/EU: 1.234,56  => thousands='.' decimal=','
      s = s.replace(/\./g, "");
      s = s.replace(",", ".");
    } else {
      // format US: 1,234.56 => thousands=',' decimal='.'
      s = s.replace(/,/g, "");
      // '.' tetap sebagai desimal
    }
  } else if (hasComma && !hasDot) {
    // hanya comma -> bisa desimal atau ribuan
    const parts = s.split(",");
    if (parts.length === 2 && parts[1].length <= 2) {
      // desimal: 100000,00
      s = s.replace(",", ".");
    } else {
      // ribuan: 100,000
      s = s.replace(/,/g, "");
    }
  } else if (hasDot && !hasComma) {
    // hanya dot -> bisa desimal atau ribuan
    const parts = s.split(".");
    const last = parts[parts.length - 1] || "";
    if (parts.length >= 2 && last.length <= 2) {
      // desimal: 100000.00
      // '.' tetap sebagai desimal
    } else {
      // ribuan: 100.000.000
      s = s.replace(/\./g, "");
    }
  }

  // buang karakter selain digit dan titik desimal (untuk parseFloat)
  s = s.replace(/[^0-9.]/g, "");
  if (!s) return null;

  const n = Number.parseFloat(s);
  if (!isFinite(n)) return null;

  // Rupiah disimpan sebagai integer (abaikan desimal)
  return Math.round(n);
}

function toPriceInputString(value) {
  const n = parseRupiahToInt(value);
  return n === null ? "" : String(n);
}

function resolvePhotoUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return null;
  if (raw.startsWith("blob:")) return null;
  if (raw.startsWith("data:")) return raw;
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      if (u.pathname.startsWith("/uploads/")) {
        return `${API}${u.pathname}`;
      }
      if (u.pathname.startsWith("/api/uploads/")) {
        return `${API}${u.pathname.replace(/^\/api/, "")}`;
      }
    } catch {
      return raw;
    }
    return raw;
  }
  if (raw.startsWith("/uploads/")) return `${API}${raw}`;
  if (raw.startsWith("/api/uploads/")) return `${API}${raw.replace(/^\/api/, "")}`;
  if (raw.startsWith("/")) return API_ORIGIN ? `${API_ORIGIN}${raw}` : raw;
  return API_ORIGIN ? `${API_ORIGIN}/${raw}` : raw;
}

function ImageWithFallback({ src, alt }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return <div className="biz-card-noimg">No Photo</div>;
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

function parseOptionsJson(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
  } catch {
    return String(value)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
}

function buildBusinessFormData(payload, photoFile) {
  const fd = new FormData();
  fd.append("user_id", String(payload.user_id));
  fd.append("category", payload.category || "");
  fd.append("title", payload.title || "");
  fd.append("capacity", payload.capacity === null ? "" : String(payload.capacity));
  fd.append("options", JSON.stringify(payload.options || []));
  fd.append("notes", payload.notes || "");
  fd.append("status", payload.status || "active");
  fd.append("unit_price", payload.unit_price === null ? "" : String(payload.unit_price));
  if (photoFile) fd.append("photo", photoFile);
  if (payload.photo_data_url && String(payload.photo_data_url).startsWith("data:image/")) {
    fd.append("photo_data_url", payload.photo_data_url);
  }
  return fd;
}

export default function BisnisSaya() {
  const { user } = useContext(AuthContext);
  const role = String(user?.role || "").toLowerCase();
  const isFieldTeam = role === "field team";
  const isAdmin = role === "admin";
  const isScopedViewer = isFieldTeam || isAdmin;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assistedUsers, setAssistedUsers] = useState([]);
  const [selectedSahabatId, setSelectedSahabatId] = useState("");

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(defaultForm);

  const [filterCat, setFilterCat] = useState("ALL");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!user?.id) return;

    const load = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${API}/business`, {
          params: isScopedViewer
            ? { scope: isAdmin ? "all" : "institution" }
            : { user_id: user.id },
          headers: authHeaders(user),
        });
        const rows = data?.data || data?.services || [];
        setItems(rows);
      } catch (err) {
        console.error(err);
        alert("Gagal memuat data Bisnis Saya.");
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isAdmin, isScopedViewer]);

  useEffect(() => {
    if (!user?.id || !isFieldTeam) return;

    axios.get(`${API}/user/sahabat-kakao`, { headers: authHeaders(user) })
      .then(({ data }) => {
        const rows = data?.data || [];
        setAssistedUsers(rows);
        setSelectedSahabatId((current) => current || (rows[0]?.id ? String(rows[0].id) : ""));
      })
      .catch((err) => {
        console.error(err);
        setAssistedUsers([]);
      });
  }, [user, isFieldTeam]);

  const onAdd = () => {
    if (isFieldTeam && !selectedSahabatId) {
      alert("Belum ada Sahabat Kakao dari institusi yang sama untuk didampingi.");
      return;
    }
    setForm({
      ...defaultForm,
      user_id: isFieldTeam ? Number(selectedSahabatId) : user?.id,
    });
    setOpen(true);
  };

  const onEdit = (row) => {
    const rawPrice = row.unit_price ?? row.unitPrice ?? "";
    const options = parseOptionsJson(row.options || row.options_json);
    const kakaoKnown = options.filter((item) => KAKAO_VARIETIES.includes(item));
    const jasaKnown = options.find((item) => JASA_OPTIONS.includes(item)) || "";
    const kakaoOther = options.filter((item) => !KAKAO_VARIETIES.includes(item)).join(", ");
    const jasaOther = options.filter((item) => !JASA_OPTIONS.includes(item)).join(", ");
    setForm({
      ...defaultForm,
      ...row,
      id: row.id,
      category: row.category || defaultForm.category,
      title: row.title || "",
      capacity: row.capacity || "",
      // ✅ pastikan input tidak tampil 100000.00
      unit_price: toPriceInputString(rawPrice),
      notes: row.notes || "",
      status: row.status || "active",
      photo_url: row.photo_url || row.photoUrl || "",
      photo_file: null,
      photo_data_url: "",
      kakaoSelected: row.category === "Bibit Kakao" ? kakaoKnown : [],
      kakaoOther: row.category === "Bibit Kakao" ? kakaoOther : "",
      jasaSelected: row.category === "Jasa" ? jasaKnown : "",
      jasaOther: row.category === "Jasa" ? jasaOther : "",
    });
    setOpen(true);
  };

  const onDelete = async (row) => {
    const ok = window.confirm("Hapus item ini?");
    if (!ok) return;

    try {
      await axios.delete(`${API}/business/${row.id}`, {
        data: { user_id: user.id },
        headers: authHeaders(user),
      });
      setItems((prev) => prev.filter((x) => x.id !== row.id));
    } catch (err) {
      console.error(err);
      alert("Gagal menghapus data.");
    }
  };

  const toggleArr = (arr, val) => {
    if (arr.includes(val)) return arr.filter((x) => x !== val);
    return [...arr, val];
  };

  const cardTitle = (row) => row?.category || row?.title || "-";

  const cardDetail = (row) => {
    if (!row) return "-";
    const opts = parseOptionsJson(row.options || row.options_json);
    if (row.category === "Bibit Kakao") {
      return `Varietas: ${opts.length ? opts.join(", ") : row.title || "-"}`;
    }
    if (row.category === "Jasa") {
      return `Jenis jasa: ${opts.length ? opts.join(", ") : row.title || "-"}`;
    }
    return row.title || row.notes || "-";
  };

  const getUnitPrice = (row) => {
    const raw = row?.unit_price ?? row?.unitPrice ?? null;
    return parseRupiahToInt(raw);
  };

  const getPhotoUrl = (row) => resolvePhotoUrl(row.photo_url || row.photoUrl || null);

  const buildTitle = () => {
    const cat = form.category;

    if (cat === "Bibit Kakao") {
      const arr = [...(form.kakaoSelected || [])];
      if (form.kakaoOther.trim()) arr.push(form.kakaoOther.trim());
      return arr.length ? `Bibit Kakao: ${arr.join(", ")}` : "Bibit Kakao";
    }
    if (cat === "Jasa") {
      return form.jasaOther.trim() || form.jasaSelected || "Jasa";
    }
    return form.title.trim() || cat;
  };

  const handleSubmit = async () => {
    if (!user?.id) return;

    const targetUserId = isFieldTeam
      ? Number(form.user_id || selectedSahabatId)
      : Number(user.id);
    if (!targetUserId) {
      alert("Pilih Sahabat Kakao yang akan didampingi.");
      return;
    }

    const cat = form.category;
    let options = [];

    if (cat === "Bibit Kakao") {
      options = [...(form.kakaoSelected || [])];
      if (form.kakaoOther.trim()) options.push(form.kakaoOther.trim());
    } else if (cat === "Jasa") {
      const selectedJasa = form.jasaOther.trim() || form.jasaSelected;
      if (selectedJasa) options = [selectedJasa];
    }

    const payload = {
      user_id: targetUserId,
      category: cat,
      title: buildTitle(),
      capacity: form.capacity ? Number(form.capacity) : null,
      options,
      notes: form.notes?.trim() || null,
      status: form.status || "active",
      // ✅ aman untuk "100000.00" / "100.000" / "Rp 100.000"
      unit_price: parseRupiahToInt(form.unit_price),
      photo_data_url: form.photo_data_url,
    };
    const body = buildBusinessFormData(payload, form.photo_file);

    try {
      setSaving(true);

      if (form.id) {
        const { data } = await axios.put(`${API}/business/${form.id}`, body, {
          headers: authHeaders(user),
        });
        const updated = data?.item || data?.data || {};
        setItems((prev) =>
          prev.map((x) => (x.id === form.id ? { ...x, ...updated } : x))
        );
      } else {
        const { data } = await axios.post(`${API}/business`, body, {
          headers: authHeaders(user),
        });
        const owner = assistedUsers.find((item) => Number(item.id) === targetUserId);
        const created = {
          ...(data?.item || data?.data),
          provider_name: owner?.full_name,
          provider_institution: owner?.institution,
        };
        setItems((prev) => [created, ...prev]);
      }

      setOpen(false);
      setForm(defaultForm);
    } catch (err) {
      console.error(err);
      alert("Gagal menyimpan data.");
    } finally {
      setSaving(false);
    }
  };

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (items || []).filter((it) => {
      if (isFieldTeam && selectedSahabatId && Number(it.user_id) !== Number(selectedSahabatId)) return false;
      if (filterCat !== "ALL" && it.category !== filterCat) return false;
      if (!q) return true;
      const options = parseOptionsJson(it.options || it.options_json).join(" ");
      const hay = [it.title, it.category, options, it.notes, it.provider_name, it.provider_institution]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [items, filterCat, query, isFieldTeam, selectedSahabatId]);

  const onPickPhoto = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({
        ...prev,
        photo_file: file,
        photo_data_url: String(reader.result || ""),
      }));
    };
    reader.readAsDataURL(file);
  };

  const previewPhotoSrc = form.photo_data_url || resolvePhotoUrl(form.photo_url);

  return (
    <div className="wrap">
      <div className="biz-headerbar">
        <div className="biz-header-left">
          <div>
            <h2 className="biz-title">{isScopedViewer ? "Bisnis Sahabat Kakao" : "Bisnis Saya"}</h2>
            {isScopedViewer && (
              <div className="biz-scope-note">
                {isAdmin ? "Semua institusi" : `Institusi: ${user?.institution || "sesuai akun"}`}
              </div>
            )}
          </div>
          <span className="biz-count">
            {loading ? "Memuat…" : `${filteredItems.length} item`}
          </span>
        </div>

        <div className="biz-header-mid">
          {isFieldTeam && (
            <div className="biz-control biz-assist-control">
              <span className="lbl">Sahabat Kakao</span>
              <select
                value={selectedSahabatId}
                onChange={(e) => setSelectedSahabatId(e.target.value)}
                aria-label="Pilih Sahabat Kakao yang didampingi"
              >
                {assistedUsers.length === 0 && <option value="">Belum ada akun</option>}
                {assistedUsers.map((member) => (
                  <option key={member.id} value={member.id}>{member.full_name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="biz-control">
            <span className="lbl">Filter</span>
            <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)}>
              <option value="ALL">Semua</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="biz-search">
            <input
              type="search"
              placeholder="Cari item…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button
                className="biz-search-clear"
                onClick={() => setQuery("")}
                title="Bersihkan"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {(!isScopedViewer || isFieldTeam) && <div className="biz-header-right">
          <button className="btn btn-add-service" onClick={onAdd} disabled={isFieldTeam && !selectedSahabatId}>
            + Tambah Layanan Usaha
          </button>
        </div>}
      </div>

      {loading ? (
        <div className="muted" style={{ marginTop: "0.75rem" }}>
          Memuat…
        </div>
      ) : filteredItems.length === 0 ? (
        <p className="muted" style={{ marginTop: "0.75rem" }}>
          Tidak ada data yang cocok. Coba ubah filter/pencarian.
        </p>
      ) : (
        <div className="biz-grid">
          {filteredItems.map((row) => {
            const price = getUnitPrice(row);
            const photoUrl = getPhotoUrl(row);
            const createdAt = row.created_at
              ? new Date(row.created_at).toLocaleDateString()
              : null;

            return (
              <div className="biz-card" key={row.id}>
                <div className="biz-card-media">
                  <ImageWithFallback
                    src={photoUrl}
                    alt={row.title || row.category || "Foto produk"}
                  />
                </div>

                <div className="biz-card-body">
                  <div className="biz-card-title" title={cardTitle(row)}>
                    {cardTitle(row)}
                  </div>

                  {isScopedViewer && (
                    <div className="biz-card-provider">
                      {row.provider_name || "Sahabat Kakao"}
                      {row.provider_institution ? ` - ${row.provider_institution}` : ""}
                    </div>
                  )}

                  <div className="biz-card-desc" title={cardDetail(row)}>
                    {cardDetail(row)}
                  </div>

                  <div className="biz-card-meta">
                    <div className="biz-card-price">{formatRupiah(price)}</div>
                    <div className="biz-card-cap">
                      {row.capacity ? `Kapasitas: ${formatWholeNumber(row.capacity)}` : "Kapasitas: —"}
                    </div>
                  </div>

                  <div className="biz-card-foot">
                    <span className="biz-card-date">
                      {createdAt ? `Dibuat: ${createdAt}` : "—"}
                    </span>

                    {(!isScopedViewer || isFieldTeam) && <div className="biz-card-actions">
                      <button className="btn-alt" onClick={() => onEdit(row)}>
                        Edit
                      </button>
                      {!isFieldTeam && (
                        <button className="btn-danger" onClick={() => onDelete(row)}>
                          Hapus
                        </button>
                      )}
                    </div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL */}
      {open && (
        <div className="modal-backdrop" onClick={() => !saving && setOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">
                {form.id ? "Edit Layanan Usaha" : "Tambah Layanan Usaha"}
              </div>
              <button
                className="modal-close"
                onClick={() => !saving && setOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body scrollable">
              <label className="mb-2">
                <span>Jenis Usaha</span>
                <select
                  value={form.category}
                  onChange={(e) =>
                    setForm({ ...defaultForm, category: e.target.value, id: form.id })
                  }
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>

              {form.category === "Bibit Kakao" && (
                <>
                  <div className="choice-grid mb-2">
                    {KAKAO_VARIETIES.map((v) => (
                      <label key={v} className="check">
                        <input
                          type="checkbox"
                          checked={form.kakaoSelected.includes(v)}
                          onChange={() =>
                            setForm({
                              ...form,
                              kakaoSelected: toggleArr(form.kakaoSelected, v),
                            })
                          }
                        />
                        <span>{v}</span>
                      </label>
                    ))}
                  </div>

                  <label className="mb-2">
                    <span>Lainnya (opsional)</span>
                    <input
                      type="text"
                      placeholder="Contoh: MCC02/45"
                      value={form.kakaoOther}
                      onChange={(e) => setForm({ ...form, kakaoOther: e.target.value })}
                    />
                  </label>
                </>
              )}

              {form.category === "Jasa" && (
                <>
                  <label className="mb-2">
                    <span>Jenis Jasa</span>
                    <select
                      value={form.jasaSelected}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          jasaSelected: e.target.value,
                          jasaOther: e.target.value ? "" : form.jasaOther,
                        })
                      }
                    >
                      <option value="">Pilih jenis jasa</option>
                      {JASA_OPTIONS.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="mb-2">
                    <span>Lainnya (opsional)</span>
                    <input
                      type="text"
                      placeholder="Contoh: Pendampingan usaha"
                      value={form.jasaOther}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          jasaOther: e.target.value,
                          jasaSelected: e.target.value ? "" : form.jasaSelected,
                        })
                      }
                    />
                  </label>
                </>
              )}

              {form.category !== "Bibit Kakao" && form.category !== "Jasa" && (
                <label className="mb-2">
                  <span>Judul</span>
                  <input
                    type="text"
                    placeholder="Nama produk/layanan"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                  />
                </label>
              )}

              <div className="grid-2 gap-2 mb-2 field-inline-pricing">
                <label>
                  <span>Kapasitas (opsional)</span>
                  <input
                    type="number"
                    value={form.capacity}
                    onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                    placeholder="contoh: 100"
                  />
                </label>

                <label>
                  <span>Harga (Rp)</span>
                  <input
                    type="text"
                    value={form.unit_price}
                    onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                    placeholder="contoh: 25000 atau 25.000"
                    inputMode="numeric"
                  />
                </label>
              </div>

              <label className="mb-2 field-notes">
                <span>Catatan (opsional)</span>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Deskripsi singkat..."
                />
              </label>

              <div className="grid-2 gap-2 mb-2">
                <label>
                  <span>Status</span>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="expired">Expired</option>
                  </select>
                </label>

                <label className="photo-field">
                  <span>Foto Produk (opsional)</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => onPickPhoto(e.target.files?.[0])}
                  />
                  {previewPhotoSrc && (
                    <div className="preview">
                      <img src={previewPhotoSrc} alt="Preview foto produk" />
                    </div>
                  )}
                </label>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-alt" onClick={() => !saving && setOpen(false)}>
                Batal
              </button>
              <button className="btn" onClick={handleSubmit} disabled={saving}>
                {saving ? "Menyimpan…" : "Simpan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
